"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcn/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/shadcn/ui/popover";
import { Label } from "@/shadcn/ui/label";
import { Switch } from "@/shadcn/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcn/ui/select";

import type { IPrices } from "@/sanity/pages/PageHomeTemplate";

import titleDetail from "@/public/home-v2/double-arrows.svg";
import bannerBG from "@/public/global/price-table-banner-bg.png";
import arrowRight from "@/public/global/big-arrow-right.svg";

import arrowRow from "@/public/home/arrow-row-2.svg";
import arrowRowFutures from "@/public/home/arrow-row-2-futures.svg";
import arrowRowV from "@/public/home/arrow-row-vertical.svg";
import arrowRowVFutures from "@/public/home/arrow-row-vertical-futures.svg";
import connectors from "@/public/global/connectors.svg";
import { PortableTextBlock } from "sanity";
import { GROQButton } from "@/sanity/lib/definitions";
import { PortableText } from "next-sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import flashSalebg from "@/public/home/flashSalebg.png";
import flashSalebgM from "@/public/home/flashSalebgM.png";
import flashSaleStatue from "@/public/home/flashSalestatue.png";

export default function PricingTable({
  data,
  stepOneTitle,
  stepTwoTitle,
  stepThreeTitle,
  stepPriceText,
  lang,
  showBanner,
  showFlashSaleBanner,
  flashSaleBannerTitle,
  flashSaleBannerText,
  flashSaleBannerCTA,
  futures = false,
}: {
  data: IPrices[];
  stepOneTitle: string;
  stepTwoTitle: string;
  stepThreeTitle: string;
  stepPriceText?: string;
  lang: "en" | "de" | "br";
  showBanner?: boolean;
  showFlashSaleBanner?: boolean;
  flashSaleBannerTitle?: PortableTextBlock[];
  flashSaleBannerText?: PortableTextBlock[];
  flashSaleBannerCTA?: GROQButton;
  futures?: boolean;
}) {
  const [selectedFundingMode, setSelectedFundingMode] = useState<
    "Evaluation" | "Instant Funding"
  >("Evaluation");
  const [selectedAccount, setSelectedAccount] = useState<IPrices>();
  const [noDailyDrawDown, setNoDailyDrawDown] = useState<boolean>(false);
  const [quickComparison, setQuickComparison] = useState<boolean>(false);

  useEffect(() => {
    setSelectedAccount(data.find((each) => each.type === selectedFundingMode));
  }, [selectedFundingMode, data, quickComparison]);

  function fundingModeToStringLocalized(fundingMode: string) {
    if (lang !== "br") {
      return fundingMode;
    }

    if (fundingMode === "Evaluation") return "Avaliação";
    if (fundingMode === "Instant Funding") return "Financiamento Instantâneo";
  }

  const tooltip = (title: React.ReactNode, content: React.ReactNode) => (
    <div className="hidden lg:block">
      <Tooltip>
        <TooltipTrigger asChild>{title}</TooltipTrigger>
        <TooltipContent className="bg-transparent">{content}</TooltipContent>
      </Tooltip>
    </div>
  );

  const popover = (title: React.ReactNode, content: React.ReactNode) => (
    <div className="mt-2 lg:hidden">
      <Popover>
        <PopoverTrigger>{title}</PopoverTrigger>
        <PopoverContent className="w-auto bg-transparent border-none shadow-none">
          {content}
        </PopoverContent>
      </Popover>
    </div>
  );

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex flex-col w-full gap-[2rem]">
        <div className="flex relative flex-col lg:flex-row bg-white/90 p-[2.5rem] rounded-[2rem] gap-[2.5rem] sm:p-[3rem] lg:p-[4rem] lg:rounded-[3rem] lg:gap-[4rem]">
          {/* step 1 */}
          <div className={cn("flex flex-col w-full gap-[2.5rem] px-[2rem] py-[2rem] rounded-[1.5rem] lg:rounded-[2.25rem] lg:gap-[3rem] lg:p-[3rem]", futures ? "bg-[#EAEAF6]/40" : "bg-[#E6EDE9]/40")}>
            <h4 className="flex flex-col mb-0 gap-[.5rem] font-geist">
              <strong className="font-bold text-[#3F553B] text-[2.5rem]">
                {lang !== "br" ? "STEP" : "PASSO"} 1
              </strong>
              <span className="italic font-normal text-[2rem] text-black/60">
                {stepOneTitle}
              </span>
            </h4>

            <Select
              value={selectedFundingMode}
              onValueChange={(value: "Evaluation" | "Instant Funding") =>
                setSelectedFundingMode(value)
              }
            >
              <SelectTrigger className="w-full h-auto bg-white text-[2rem] rounded-[1.5rem] border-[.25rem] border-[#DEDEDE] px-[2.75rem] py-[1.5rem] lg:rounded-[2rem]">
                <SelectValue placeholder="Select funding mode" />
              </SelectTrigger>
              <SelectContent className="rounded-[1.5rem] border-[.25rem] border-[#DEDEDE]">
                <SelectItem
                  value="Evaluation"
                  className="cursor-pointer text-[2rem] px-[2rem] py-[1.25rem] hover:text-[#6DA991] focus:bg-white focus:text-[#6DA991]"
                >
                  {fundingModeToStringLocalized("Evaluation")}
                </SelectItem>
                <SelectItem
                  value="Instant Funding"
                  className="cursor-pointer text-[2rem] px-[2rem] py-[1.25rem] hover:text-[#6DA991] focus:bg-white focus:text-[#6DA991]"
                >
                  {fundingModeToStringLocalized("Instant Funding")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* end step 1 */}

          <Image
            src={connectors}
            alt=""
            className="hidden absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 lg:block w-[4.8rem]"
          />

          {/* step 2 */}
          <div
            className={cn(
              "flex flex-col w-full gap-[2.5rem] px-[2rem] py-[2rem] rounded-[1.5rem] lg:rounded-[2.25rem] lg:gap-[3rem] lg:p-[3rem]",
              futures ? "bg-[#EAEAF6]/40" : "bg-[#E6EDE9]/40",
              quickComparison &&
                "cursor-not-allowed pointer-events-none opacity-75",
            )}
          >
            <h4 className="flex flex-col mb-0 gap-[.5rem] font-geist">
              <strong className="font-bold text-[#3F553B] text-[2.5rem]">
                {lang !== "br" ? "STEP" : "PASSO"} 2
              </strong>
              <span className="italic font-normal text-[2rem] text-black/60">
                {stepTwoTitle}
              </span>
            </h4>

            <Select
              value={selectedAccount?.balance?.toString() ?? ""}
              onValueChange={(value: string) => {
                const account = data.find(
                  (each) =>
                    each.type === selectedFundingMode &&
                    each.balance.toString() === value,
                );
                if (account) setSelectedAccount(account);
              }}
              disabled={quickComparison}
            >
              <SelectTrigger className="w-full h-auto bg-white text-[2rem] rounded-[1.5rem] border-[.25rem] border-[#DEDEDE] px-[2.75rem] py-[1.5rem] lg:rounded-[2rem]">
                <SelectValue placeholder="Select account size" />
              </SelectTrigger>
              <SelectContent className="rounded-[1.5rem] border-[.25rem] border-[#DEDEDE]">
                {data
                  .filter((each) => each.type === selectedFundingMode)
                  .map((price, index) => (
                    <SelectItem
                      key={index}
                      value={price.balance.toString()}
                      className="cursor-pointer text-[2rem] px-[2rem] py-[1.25rem] group-hover:!text-[#6DA991] focus:bg-white focus:text-[#6DA991] [&_.littlesquare]:hover:!bg-[#6DA991] [&_.littlesquare]:bg-[#10303780]"
                    >
                      <span className="flex items-center">
                        {price.name}
                        <span className="mx-[1rem] w-[0.5rem] h-[0.5rem] rounded-[0.1rem] shrink-0 bg-[#10303780] littlesquare"></span>
                        <span
                          className={cn(
                            "font-[600]",
                            lang === "de" ? "flex flex-row-reverse" : "flex",
                          )}
                        >
                          <span>$</span>
                          {price.balance.toLocaleString("en-US")}
                        </span>
                      </span>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          {/* end step 2 */}
        </div>
        {/* table */}
        <div className="flex flex-col w-full bg-white/90 gap-[1.5rem] p-[2.5rem] rounded-[2rem] sm:p-[3rem] lg:p-[4rem] lg:rounded-[3rem]">
          {/* summary */}
          <div className="grid w-full lg:grid-cols-7 gap-[1.5rem]">
            <div className={cn("hidden justify-center items-center border lg:flex lg:col-span-2 gap-[1.5rem] border-[#DEDEDE80] rounded-[1.5rem] px-[2rem] py-[2.5rem] lg:rounded-[2rem]", futures ? "bg-[#EAEAF6]/40" : "bg-[#E6EDE9]")}>
              <Switch
                id="quick-comparison"
                className="w-[4.75rem] h-[2.5rem] data-[state=checked]:bg-[#E6EDE9] [&_.thumb]:h-[1.75rem] [&_.thumb]:w-[1.75rem] [&_.thumb]:data-[state=checked]:translate-x-9 border border-black/10"
                checked={quickComparison}
                onCheckedChange={setQuickComparison}
              />
              <Label
                htmlFor="quick-comparison"
                className="text-black cursor-pointer text-[2rem]"
              >
                {lang !== "br" ? "Quick Comparison" : "Comparação Rápida"}
              </Label>
            </div>

            {!quickComparison && (
              <div className="flex flex-col justify-center items-center border lg:flex-row lg:col-span-5 gap-[2rem] border-[#DEDEDE80] rounded-[1.5rem] px-[2rem] py-[2rem] lg:rounded-[2rem]">
                {/* type */}
                <div className={cn("w-full text-center rounded-[1.125rem] px-[1.5rem] py-[.75rem] lg:w-fit", futures ? "bg-[#EAEAF6]/40" : "bg-[#E6EDE9]")}>
                  <span className="text-black/80 text-[2rem] font-[500]">
                    {fundingModeToStringLocalized(selectedFundingMode)}
                  </span>
                </div>
                {/* end type */}

                {/* name */}
                <div className={cn("w-full text-center rounded-[1.125rem] px-[1.5rem] py-[.75rem] lg:w-fit", futures ? "bg-[#EAEAF6]/40" : "bg-[#E6EDE9]")}>
                  <span className="text-black/80 text-[2rem] font-[500]">
                    {selectedAccount?.name}
                  </span>
                </div>
                {/* end name */}

                {/* drawdown */}
                <div className={cn("w-full text-center rounded-[1.125rem] px-[1.5rem] py-[.75rem] lg:w-fit", futures ? "bg-[#EAEAF6]/40" : "bg-[#E6EDE9]")}>
                  <span className="text-black/80 text-[2rem] font-[500]">
                    Max DrawDown
                    <span className="mx-2 opacity-50">|</span>
                    <span className="font-[600]">
                      {selectedAccount?.maxDrawDownPercentage}%
                    </span>
                  </span>
                </div>
              </div>
            )}

            {quickComparison && (
              <>
                {data
                  .filter((each) => each.type === selectedFundingMode)
                  .map((price, index) => (
                    <div
                      key={index}
                      className="flex justify-center items-center border lg:col-span-1 border-[#DEDEDE80] rounded-[1.5rem] py-[2rem] lg:rounded-[2rem]"
                    >
                      <div className={cn("w-full text-center rounded-[1.125rem] px-[1.5rem] py-[.75rem] lg:w-fit", futures ? "bg-[#EAEAF6]/40" : "bg-[#E6EDE9]")}>
                        <span className="text-black/70 text-[2rem] font-[500]">
                          {price.name}
                        </span>
                      </div>
                    </div>
                  ))}
              </>
            )}
          </div>
          {/* end summary */}
          {/* balance */}
          <div className="grid w-full border lg:grid-cols-7 lg:p-0 lg:border-none border-[#DEDEDE80] rounded-[1.5rem] p-[1rem] lg:gap-[1.5rem]">
            <div className="flex justify-between items-center lg:col-span-2 lg:border gap-[1.5rem] rounded-[1.5rem] px-[2rem] py-[.75rem] lg:border-[#DEDEDE80] lg:rounded-[2rem] lg:py-[2.5rem]">
              <span className="font-sf text-[2.05rem] font-[600] text-[#2C2C2C]">
                {lang !== "br" ? "Account Balance" : "Saldo da conta"}
              </span>

              {tooltip(
                <div className="relative rounded-full border cursor-pointer group p-[0.625rem] border-[#C4C4C4] h-[2.2rem] w-[2.2rem] hover:bg-[#07171B] hover:border-[#EFEFEF]">
                  <p className="absolute top-0 left-1/2 m-0 uppercase -translate-x-1/2 group-hover:text-white text-[#9A9A9A] text-[1.5rem]">
                    ?
                  </p>
                </div>,
                <div className="text-white shadow-lg p-[2rem] bg-[#07171B] z-[9999] rounded-[1.5rem]">
                  <p className="m-0 text-[2rem] w-[35.5rem]">
                    {lang !== "br"
                      ? "The amount you'll trade with after passing the challenge — also determines your potential payout base."
                      : "O valor que você utilizará para negociar após concluir o desafio também determina sua base de pagamento potencial."}
                  </p>
                </div>,
              )}

              {popover(
                <div className="relative rounded-full border cursor-pointer group p-[0.625rem] border-[#C4C4C4] h-[3rem] w-[3rem] hover:bg-[#07171B] hover:border-[#EFEFEF]">
                  <span className="absolute top-1/2 left-1/2 m-0 uppercase -translate-x-1/2 -translate-y-1/2 group-hover:text-white text-[#9A9A9A] text-[2rem]">
                    ?
                  </span>
                </div>,
                <div className="text-white shadow-lg p-[2rem] bg-[#07171B] z-[9999] rounded-[1.5rem]">
                  <p className="m-0 text-[2rem] w-[35.5rem]">
                    {lang !== "br"
                      ? "The amount you'll trade with after passing the challenge — also determines your potential payout base."
                      : "O valor que você utilizará para negociar após concluir o desafio também determina sua base de pagamento potencial."}
                  </p>
                </div>,
              )}
            </div>

            {!quickComparison && (
              <div className="flex items-center lg:col-span-5 lg:justify-center lg:border gap-[2rem] rounded-[1.5rem] px-[2rem] py-[.75rem] lg:border-[#DEDEDE80] lg:rounded-[2rem] lg:py-[2.5rem]">
                <p
                  className={cn(
                    "font-sf text-[2rem] font-[500] text-black/80 mb-0",
                    lang === "de" ? "flex flex-row-reverse" : "flex",
                  )}
                >
                  <span>$</span>
                  {selectedAccount?.balance?.toLocaleString("en-US")}
                </p>
              </div>
            )}

            {quickComparison && (
              <>
                {data
                  .filter((each) => each.type === selectedFundingMode)
                  .map((price, index) => (
                    <div
                      key={index}
                      className="flex justify-center items-center border lg:col-span-1 border-[#DEDEDE80] rounded-[1.5rem] py-[2rem] lg:rounded-[2rem]"
                    >
                      <p
                        className={cn(
                          "text-black/70 text-[2rem] font-[500] mb-0",
                          lang === "de" ? "flex flex-row-reverse" : "flex",
                        )}
                      >
                        <span>$</span>
                        {price?.balance?.toLocaleString("en-US")}
                      </p>
                    </div>
                  ))}
              </>
            )}
          </div>
          {/* end balance */}
          {/* profit target */}
          {selectedFundingMode === "Evaluation" && (
            <div className="grid w-full border lg:grid-cols-7 lg:p-0 lg:border-none border-[#DEDEDE80] rounded-[1.5rem] p-[1rem] lg:gap-[1.5rem]">
              <div className="flex justify-between items-center lg:col-span-2 lg:border gap-[1.5rem] rounded-[1.5rem] px-[2rem] py-[.75rem] lg:border-[#DEDEDE80] lg:rounded-[2rem] lg:py-[2.5rem]">
                <span className="font-sf text-[2.05rem] font-[600] text-[#2C2C2C]">
                  {lang !== "br" ? "Profit Target" : "Meta de lucro"}
                </span>

                {tooltip(
                  <div className="relative rounded-full border cursor-pointer group p-[0.625rem] border-[#C4C4C4] h-[2.2rem] w-[2.2rem] hover:bg-[#07171B] hover:border-[#EFEFEF]">
                    <p className="absolute top-0 left-1/2 m-0 uppercase -translate-x-1/2 group-hover:text-white text-[#9A9A9A] text-[1.5rem]">
                      ?
                    </p>
                  </div>,
                  <div className="text-white shadow-lg p-[2rem] bg-[#07171B] z-[9999] rounded-[1.5rem]">
                    <p className="m-0 text-[2rem] w-[35.5rem]">
                      {lang !== "br"
                        ? `You need to generate ${selectedAccount?.profitPercentageGoal}% profit on your demo account, within the rules, to qualify for funding.`
                        : `Para se qualificar para o financiamento, você precisa gerar um lucro de ${selectedAccount?.profitPercentageGoal}% em sua conta de demonstração, dentro das regras.`}
                    </p>
                  </div>,
                )}

                {popover(
                  <div className="relative rounded-full border cursor-pointer group p-[0.625rem] border-[#C4C4C4] h-[3rem] w-[3rem] hover:bg-[#07171B] hover:border-[#EFEFEF]">
                    <span className="absolute top-1/2 left-1/2 m-0 uppercase -translate-x-1/2 -translate-y-1/2 group-hover:text-white text-[#9A9A9A] text-[2rem]">
                      ?
                    </span>
                  </div>,
                  <div className="text-white shadow-lg p-[2rem] bg-[#07171B] z-[9999] rounded-[1.5rem]">
                    <p className="m-0 text-[2rem] w-[35.5rem]">
                      {lang !== "br"
                        ? `You need to generate ${selectedAccount?.profitPercentageGoal}% profit on your demo account, within the rules, to qualify for funding.`
                        : `Para se qualificar para o financiamento, você precisa gerar um lucro de ${selectedAccount?.profitPercentageGoal}% em sua conta de demonstração, dentro das regras.`}
                    </p>
                  </div>,
                )}
              </div>

              {!quickComparison && (
                <div className="flex items-center lg:col-span-5 lg:justify-center lg:border gap-[2rem] rounded-[1.5rem] px-[2rem] py-[.75rem] lg:border-[#DEDEDE80] lg:rounded-[2rem] lg:py-[2.5rem]">
                  <p className="mb-0 text-[2rem] font-[500] text-black/80">
                    {selectedAccount?.profitPercentageGoal}%{" "}
                    <span className="mx-2 opacity-50">|</span>$
                    {(
                      ((selectedAccount?.balance ?? 0) *
                        (selectedAccount?.profitPercentageGoal ?? 0)) /
                      100
                    ).toLocaleString("en-US")}
                  </p>
                </div>
              )}

              {quickComparison && (
                <>
                  {data
                    .filter((each) => each.type === selectedFundingMode)
                    .map((price, index) => (
                      <div
                        key={index}
                        className="flex justify-center items-center border lg:col-span-1 border-[#DEDEDE80] rounded-[1.5rem] py-[2rem] lg:rounded-[2rem]"
                      >
                        <p className="mb-0 text-black/70 text-[2rem] font-[500]">
                          {price?.profitPercentageGoal}%{" "}
                          <span className="mx-2 opacity-50">|</span>$
                          {(
                            ((price?.balance ?? 0) *
                              (price?.profitPercentageGoal ?? 0)) /
                            100
                          ).toLocaleString("en-US")}
                        </p>
                      </div>
                    ))}
                </>
              )}
            </div>
          )}
          {/* end profit target */}
          {/* max daily drawdown */}
          {/* {!noDailyDrawDown && (
            <div className="grid w-full border lg:grid-cols-7 lg:p-0 lg:border-none border-[#DEDEDE80] rounded-[1.5rem] p-[1rem] lg:gap-[1.5rem]">
              <div className="flex justify-between items-center lg:col-span-2 lg:border gap-[1.5rem] rounded-[1.5rem] px-[2rem] py-[.75rem] lg:border-[#DEDEDE80] lg:rounded-[2rem] lg:py-[2.5rem]">
                <span className="font-sf text-[2.05rem] font-[600] text-[#2C2C2C]">
                  Max Daily DrawDown
                </span>

                {tooltip(
                  <div className="relative rounded-full border cursor-pointer group p-[0.625rem] border-[#C4C4C4] h-[2.2rem] w-[2.2rem] hover:bg-[#07171B] hover:border-[#EFEFEF]">
                    <p className="absolute top-0 left-1/2 m-0 uppercase -translate-x-1/2 group-hover:text-white text-[#9A9A9A] text-[1.5rem]">
                      ?
                    </p>
                  </div>,
                  <div className="text-white shadow-lg p-[2rem] bg-[#07171B] z-[9999] rounded-[1.5rem]">
                    <div className="[&_p]:m-0 [&_p]:text-[2rem] [&_p]:font-sf w-[35.5rem] space-y-[3rem]">
                      <p className="">
                        Your total allowed risk during the challenge and while
                        funded. Includes:
                      </p>
                      <div>
                        <div className="flex items-start gap-[1rem]">
                          <div className="w-[0.75rem] h-[.75rem] bg-white/80 mt-[1rem]" />
                          <p className="flex-1">
                            Max DrawDown: Total capital loss limit (
                            {selectedAccount?.maxDrawDownPercentage}%)
                          </p>
                        </div>
                        <hr className="w-full border-t border-white/10 my-[1.5rem]" />
                        <div className="flex items-start gap-[1rem]">
                          <div className="w-[0.75rem] h-[.75rem] bg-white/80 mt-[1rem]" />
                          <p className="flex-1">
                            Max Daily DrawDown: Maximum loss per day (
                            {selectedAccount?.maxDailyDrawDownPercentage}%)
                          </p>
                        </div>
                        <hr className="w-full border-t border-white/10 my-[1.5rem]" />
                        <div className="flex items-start gap-[1rem]">
                          <div className="w-[0.75rem] h-[.75rem] bg-white/80 mt-[1rem]" />
                          <p className="flex-1">
                            Max Trade Risk (Evaluation): 1% per position
                          </p>
                        </div>
                        <hr className="w-full border-t border-white/10 my-[1.5rem]" />
                        <div className="flex items-start gap-[1rem]">
                          <div className="w-[0.75rem] h-[.75rem] bg-white/80 mt-[1rem]" />
                          <p className="flex-1">
                            Max Trade Risk (Funded): 0.5% per position
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {popover(
                  <div className="relative rounded-full border cursor-pointer group p-[0.625rem] border-[#C4C4C4] h-[2.2rem] w-[2.2rem] hover:bg-[#07171B] hover:border-[#EFEFEF]">
                    <p className="absolute top-0 left-1/2 m-0 uppercase -translate-x-1/2 group-hover:text-white text-[#9A9A9A] text-[1.5rem]">
                      ?
                    </p>
                  </div>,
                  <div className="text-white shadow-lg p-[2rem] bg-[#07171B] z-[9999] rounded-[1.5rem]">
                    <div className="[&_p]:m-0 [&_p]:text-[2rem] [&_p]:font-sf w-[35.5rem] space-y-[3rem]">
                      <p className="">
                        Your total allowed risk during the challenge and while
                        funded. Includes:
                      </p>
                      <div>
                        <div className="flex items-start gap-[1rem]">
                          <div className="w-[0.75rem] h-[.75rem] bg-white/80 mt-[1rem]" />
                          <p className="flex-1">
                            Max DrawDown: Total capital loss limit (
                            {selectedAccount?.maxDrawDownPercentage}%)
                          </p>
                        </div>
                        <hr className="w-full border-t border-white/10 my-[1.5rem]" />
                        <div className="flex items-start gap-[1rem]">
                          <div className="w-[0.75rem] h-[.75rem] bg-white/80 mt-[1rem]" />
                          <p className="flex-1">
                            Max Daily DrawDown: Maximum loss per day (
                            {selectedAccount?.maxDailyDrawDownPercentage}%)
                          </p>
                        </div>
                        <hr className="w-full border-t border-white/10 my-[1.5rem]" />
                        <div className="flex items-start gap-[1rem]">
                          <div className="w-[0.75rem] h-[.75rem] bg-white/80 mt-[1rem]" />
                          <p className="flex-1">
                            Max Trade Risk (Evaluation): 1% per position
                          </p>
                        </div>
                        <hr className="w-full border-t border-white/10 my-[1.5rem]" />
                        <div className="flex items-start gap-[1rem]">
                          <div className="w-[0.75rem] h-[.75rem] bg-white/80 mt-[1rem]" />
                          <p className="flex-1">
                            Max Trade Risk (Funded): 0.5% per position
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {!quickComparison && (
                <div className="flex items-center lg:col-span-5 lg:justify-center lg:border gap-[2rem] rounded-[1.5rem] px-[2rem] py-[.75rem] lg:border-[#DEDEDE80] lg:rounded-[2rem] lg:py-[2.5rem]">
                  <span className="font-sf text-[2rem] font-[500] text-black/80">
                    {selectedAccount?.maxDailyDrawDownPercentage}%{" "}
                    <span className="mx-2 opacity-50">|</span>$
                    {(
                      ((selectedAccount?.balance ?? 0) *
                        (selectedAccount?.maxDailyDrawDownPercentage ?? 0)) /
                      100
                    ).toLocaleString("en-US")}
                  </span>
                </div>
              )}

              {quickComparison && (
                <>
                  {data
                    .filter((each) => each.type === selectedFundingMode)
                    .map((price, index) => (
                      <div
                        key={index}
                        className="flex justify-center items-center border lg:col-span-1 border-[#DEDEDE80] rounded-[1.5rem] py-[2rem] lg:rounded-[2rem]"
                      >
                        <span className="text-black/70 text-[2rem] font-[500]">
                          {price?.maxDailyDrawDownPercentage}%{" "}
                          <span className="mx-2 opacity-50">|</span>$
                          {(
                            ((price?.balance ?? 0) *
                              (price?.maxDailyDrawDownPercentage ?? 0)) /
                            100
                          ).toLocaleString("en-US")}
                        </span>
                      </div>
                    ))}
                </>
              )}
            </div>
          )} */}
          {/* end max daily drawdown */}
          {/* max drawdown */}
          {!noDailyDrawDown && (
            <div className="grid w-full border lg:grid-cols-7 lg:p-0 lg:border-none border-[#DEDEDE80] rounded-[1.5rem] p-[1rem] lg:gap-[1.5rem]">
              <div className="flex justify-between items-center lg:col-span-2 lg:border gap-[1.5rem] rounded-[1.5rem] px-[2rem] py-[.75rem] lg:border-[#DEDEDE80] lg:rounded-[2rem] lg:py-[2.5rem]">
                <span className="font-sf text-[2.05rem] font-[600] text-[#2C2C2C]">
                  Max DrawDown
                </span>

                {tooltip(
                  <div className="relative rounded-full border cursor-pointer group p-[0.625rem] border-[#C4C4C4] h-[2.2rem] w-[2.2rem] hover:bg-[#07171B] hover:border-[#EFEFEF]">
                    <p className="absolute top-0 left-1/2 m-0 uppercase -translate-x-1/2 group-hover:text-white text-[#9A9A9A] text-[1.5rem]">
                      ?
                    </p>
                  </div>,
                  <div className="text-white shadow-lg p-[2rem] bg-[#07171B] z-[9999] rounded-[1.5rem]">
                    <div className="[&_p]:m-0 [&_p]:text-[2rem] [&_p]:font-sf w-[35.5rem] space-y-[3rem]">
                      <p className="">
                        {lang !== "br"
                          ? `Your total allowed risk during the challenge and while funded. Includes:`
                          : `Seu risco total permitido durante o desafio e enquanto estiver financiado. Inclui:`}
                      </p>
                      <div>
                        <div className="flex items-start gap-[1rem]">
                          <div className="w-[0.75rem] h-[.75rem] bg-white/80 mt-[1rem]" />
                          <p className="flex-1">
                            {lang !== "br"
                              ? `Max DrawDown: Total capital loss limit (`
                              : `Max DrawDown: Limite total de perda de capital (`}
                            {selectedAccount?.maxDrawDownPercentage}%)
                          </p>
                        </div>
                        <hr className="w-full border-t border-white/10 my-[1.5rem]" />
                        <div className="flex items-start gap-[1rem]">
                          <div className="w-[0.75rem] h-[.75rem] bg-white/80 mt-[1rem]" />
                          <p className="flex-1">
                            {lang !== "br"
                              ? `Max Trade Risk (Evaluation): 1% per position`
                              : `Risco máximo de negociação (avaliação): 1% por posição`}
                          </p>
                        </div>
                        <hr className="w-full border-t border-white/10 my-[1.5rem]" />
                        <div className="flex items-start gap-[1rem]">
                          <div className="w-[0.75rem] h-[.75rem] bg-white/80 mt-[1rem]" />
                          <p className="flex-1">
                            {lang !== "br"
                              ? `Max Trade Risk (Funded): 0.5% per position`
                              : `Risco máximo de negociação (financiado): 0,5% por posição`}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>,
                )}

                {popover(
                  <div className="relative rounded-full border cursor-pointer group p-[0.625rem] border-[#C4C4C4] h-[2.2rem] w-[2.2rem] hover:bg-[#07171B] hover:border-[#EFEFEF]">
                    <p className="absolute top-0 left-1/2 m-0 uppercase -translate-x-1/2 group-hover:text-white text-[#9A9A9A] text-[1.5rem]">
                      ?
                    </p>
                  </div>,
                  <div className="text-white shadow-lg p-[2rem] bg-[#07171B] z-[9999] rounded-[1.5rem]">
                    <div className="[&_p]:m-0 [&_p]:text-[2rem] [&_p]:font-sf w-[35.5rem] space-y-[3rem]">
                      <p className="">
                        {lang !== "br"
                          ? `Your total allowed risk during the challenge and while funded. Includes:`
                          : `Seu risco total permitido durante o desafio e enquanto estiver financiado. Inclui:`}
                      </p>
                      <div>
                        <div className="flex items-start gap-[1rem]">
                          <div className="w-[0.75rem] h-[.75rem] bg-white/80 mt-[1rem]" />
                          <p className="flex-1">
                            {lang !== "br"
                              ? `Max DrawDown: Total capital loss limit (`
                              : `Max DrawDown: Limite total de perda de capital (`}
                            {selectedAccount?.maxDrawDownPercentage}%)
                          </p>
                        </div>
                        <hr className="w-full border-t border-white/10 my-[1.5rem]" />
                        <div className="flex items-start gap-[1rem]">
                          <div className="w-[0.75rem] h-[.75rem] bg-white/80 mt-[1rem]" />
                          <p className="flex-1">
                            {lang !== "br"
                              ? `Max Trade Risk (Evaluation): 1% per position`
                              : `Risco máximo de negociação (avaliação): 1% por posição`}
                          </p>
                        </div>
                        <hr className="w-full border-t border-white/10 my-[1.5rem]" />
                        <div className="flex items-start gap-[1rem]">
                          <div className="w-[0.75rem] h-[.75rem] bg-white/80 mt-[1rem]" />
                          <p className="flex-1">
                            {lang !== "br"
                              ? `Max Trade Risk (Funded): 0.5% per position`
                              : `Risco máximo de negociação (financiado): 0,5% por posição`}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>,
                )}
              </div>

              {!quickComparison && (
                <div className="flex items-center lg:col-span-5 lg:justify-center lg:border gap-[2rem] rounded-[1.5rem] px-[2rem] py-[.75rem] lg:border-[#DEDEDE80] lg:rounded-[2rem] lg:py-[2.5rem]">
                  <p className="mb-0 text-[2rem] font-[500] text-black/80">
                    {selectedAccount?.maxDrawDownPercentage}%{" "}
                    <span className="mx-2 opacity-50">|</span>$
                    {(
                      ((selectedAccount?.balance ?? 0) *
                        (selectedAccount?.maxDrawDownPercentage ?? 0)) /
                      100
                    ).toLocaleString("en-US")}
                  </p>
                </div>
              )}

              {quickComparison && (
                <>
                  {data
                    .filter((each) => each.type === selectedFundingMode)
                    .map((price, index) => (
                      <div
                        key={index}
                        className="flex justify-center items-center border lg:col-span-1 border-[#DEDEDE80] rounded-[1.5rem] py-[2rem] lg:rounded-[2rem]"
                      >
                        <p className="mb-0 text-black/70 text-[2rem] font-[500]">
                          {price?.maxDrawDownPercentage}%{" "}
                          <span className="mx-2 opacity-50">|</span>$
                          {(
                            ((price?.balance ?? 0) *
                              (price?.maxDrawDownPercentage ?? 0)) /
                            100
                          ).toLocaleString("en-US")}
                        </p>
                      </div>
                    ))}
                </>
              )}
            </div>
          )}
          {/* end max drawdown */}
          {/* max payout ratio */}
          <div className="grid w-full border lg:grid-cols-7 lg:p-0 lg:border-none border-[#DEDEDE80] rounded-[1.5rem] p-[1rem] lg:gap-[1.5rem]">
            <div className="flex justify-between items-center lg:col-span-2 lg:border gap-[1.5rem] rounded-[1.5rem] px-[2rem] py-[.75rem] lg:border-[#DEDEDE80] lg:rounded-[2rem] lg:py-[2.5rem]">
              <span className="font-sf text-[2.05rem] font-[600] text-[#2C2C2C]">
                {lang !== "br" ? "Payout Ratio" : "Índice de pagamento"}
              </span>

              {tooltip(
                <div className="relative rounded-full border cursor-pointer group p-[0.625rem] border-[#C4C4C4] h-[2.2rem] w-[2.2rem] hover:bg-[#07171B] hover:border-[#EFEFEF]">
                  <p className="absolute top-0 left-1/2 m-0 uppercase -translate-x-1/2 group-hover:text-white text-[#9A9A9A] text-[1.5rem]">
                    ?
                  </p>
                </div>,
                <div className="text-white shadow-lg p-[2rem] bg-[#07171B] z-[9999] rounded-[1.5rem]">
                  <div className="[&_p]:m-0 [&_p]:text-[2rem] [&_p]:font-sf w-[35.5rem] space-y-[3rem]">
                    <div>
                      <div className="flex items-start gap-[1rem]">
                        <div className="w-[0.75rem] h-[.75rem] bg-white/80 mt-[1rem]" />
                        <p className="flex-1">
                          {lang !== "br"
                            ? `First 10k (Max. 50% of profit balance): `
                            : `Primeiros 10 mil (máximo de 50% do saldo de lucro): `}
                          {selectedAccount?.firstPayoutRatio}%
                        </p>
                      </div>
                      <hr className="w-full border-t border-white/10 my-[1.5rem]" />
                      <div className="flex items-start gap-[1rem]">
                        <div className="w-[0.75rem] h-[.75rem] bg-white/80 mt-[1rem]" />
                        <p className="flex-1">
                          {lang !== "br"
                            ? `Payout Ratio after 10k (50% of profit remains): `
                            : `Índice de distribuição de lucros após 10 mil (50% do lucro permanece): `}
                          {selectedAccount?.payoutRatio}%
                        </p>
                      </div>
                    </div>
                    <p>
                      {lang !== "br"
                        ? `Minimum payout: $250, paid daily.`
                        : `Pagamento mínimo: US$ 250, pago diariamente.`}
                    </p>
                  </div>
                </div>,
              )}

              {popover(
                <div className="relative rounded-full border cursor-pointer group p-[0.625rem] border-[#C4C4C4] h-[2.2rem] w-[2.2rem] hover:bg-[#07171B] hover:border-[#EFEFEF]">
                  <p className="absolute top-0 left-1/2 m-0 uppercase -translate-x-1/2 group-hover:text-white text-[#9A9A9A] text-[1.5rem]">
                    ?
                  </p>
                </div>,
                <div className="text-white shadow-lg p-[2rem] bg-[#07171B] z-[9999] rounded-[1.5rem]">
                  <div className="[&_p]:m-0 [&_p]:text-[2rem] [&_p]:font-sf w-[35.5rem] space-y-[3rem]">
                    <div>
                      <div className="flex items-start gap-[1rem]">
                        <div className="w-[0.75rem] h-[.75rem] bg-white/80 mt-[1rem]" />
                        <p className="flex-1">
                          {lang !== "br"
                            ? `First 10k (Max. 50% of profit balance): `
                            : `Primeiros 10 mil (máximo de 50% do saldo de lucro): `}
                          {selectedAccount?.firstPayoutRatio}%
                        </p>
                      </div>
                      <hr className="w-full border-t border-white/10 my-[1.5rem]" />
                      <div className="flex items-start gap-[1rem]">
                        <div className="w-[0.75rem] h-[.75rem] bg-white/80 mt-[1rem]" />
                        <p className="flex-1">
                          {lang !== "br"
                            ? `Payout Ratio after 10k (50% of profit remains): `
                            : `Índice de distribuição de lucros após 10 mil (50% do lucro permanece): `}
                          {selectedAccount?.payoutRatio}%
                        </p>
                      </div>
                    </div>
                    <p>
                      {lang !== "br"
                        ? `Minimum payout: $250, paid daily.`
                        : `Pagamento mínimo: US$ 250, pago diariamente.`}
                    </p>
                  </div>
                </div>,
              )}
            </div>

            {!quickComparison && (
              <div className="flex items-center lg:col-span-5 lg:justify-center lg:border gap-[2rem] rounded-[1.5rem] px-[2rem] py-[.75rem] lg:border-[#DEDEDE80] lg:rounded-[2rem] lg:py-[2.5rem]">
                <span className="font-sf text-[2rem] font-[500] text-black/80">
                  {selectedAccount?.payoutRatio}%
                </span>
              </div>
            )}

            {quickComparison && (
              <>
                {data
                  .filter((each) => each.type === selectedFundingMode)
                  .map((price, index) => (
                    <div
                      key={index}
                      className="flex justify-center items-center border lg:col-span-1 border-[#DEDEDE80] rounded-[1.5rem] py-[2rem] lg:rounded-[2rem]"
                    >
                      <span className="text-black/70 text-[2rem] font-[500]">
                        {price?.payoutRatio}%
                      </span>
                    </div>
                  ))}
              </>
            )}
          </div>
          {/* end max payout ratio */}
          {/* max trade risk */}
          <div className="grid w-full border lg:grid-cols-7 lg:p-0 lg:border-none border-[#DEDEDE80] rounded-[1.5rem] p-[1rem] lg:gap-[1.5rem]">
            <div className="flex justify-between items-center lg:col-span-2 lg:border gap-[1.5rem] rounded-[1.5rem] px-[2rem] py-[.75rem] lg:border-[#DEDEDE80] lg:rounded-[2rem] lg:py-[2.5rem]">
              <span className="font-sf text-[2.05rem] font-[600] text-[#2C2C2C]">
                {lang !== "br"
                  ? "Max Trade Risk"
                  : "Risco máximo de negociação"}
              </span>

              {tooltip(
                <div className="relative rounded-full border cursor-pointer group p-[0.625rem] border-[#C4C4C4] h-[2.2rem] w-[2.2rem] hover:bg-[#07171B] hover:border-[#EFEFEF]">
                  <p className="absolute top-0 left-1/2 m-0 uppercase -translate-x-1/2 group-hover:text-white text-[#9A9A9A] text-[1.5rem]">
                    ?
                  </p>
                </div>,
                <div className="text-white shadow-lg p-[2rem] bg-[#07171B] z-[9999] rounded-[1.5rem]">
                  <div className="[&_p]:m-0 [&_p]:text-[2rem] [&_p]:font-sf w-[35.5rem]">
                    <div className="flex items-start gap-[1rem]">
                      <div className="w-[0.75rem] h-[.75rem] bg-white/80 mt-[1rem]" />
                      {lang === "de" ? (
                        <p className="flex-1">
                          <b>Evaluation (Challenge): </b>
                          Risiko pro Trade darf maximal 1 % des Startkapitals
                          betragen. <b>Breach = Soft</b> → die verletzende
                          Position wird automatisch geschlossen, das Konto
                          bleibt aktiv.
                        </p>
                      ) : lang === "br" ? (
                        <p className="flex-1">
                          <b>Avaliação (Desafio): </b>O risco por operação deve
                          permanecer ≤ 1% do saldo inicial da conta.
                          <b> Brecha = Leve</b> → violação A posição é fechada
                          automaticamente, a conta permanece ativa.
                        </p>
                      ) : (
                        <p className="flex-1">
                          <b>Evaluation (Challenge): </b>
                          Risk per trade must stay ≤ 1 % of the account’s
                          starting balance. <b>Breach = Soft</b> → violating
                          position is auto-closed, account remains active.
                        </p>
                      )}
                    </div>

                    <hr className="w-full border-t border-white/10 my-[1.5rem]" />

                    <div className="flex items-start gap-[1rem]">
                      <div className="w-[0.75rem] h-[.75rem] bg-white/80 mt-[1rem]" />
                      {lang === "de" ? (
                        <p className="flex-1">
                          <b>Funding: </b>
                          Risiko pro Trade darf maximal <b>0,5 %</b> des
                          Startkapitals betragen. <b>Erste 2 Verstöße = Soft</b>{" "}
                          (Position wird automatisch geschlossen, Konto bleibt
                          aktiv). <b>3. Verstoß = Hard</b> → Konto endet.
                        </p>
                      ) : lang === "br" ? (
                        <p className="flex-1">
                          <b>Financiamento: </b>O risco por operação deve
                          permanecer ≤ <b>0,5%</b> do saldo inicial da conta.{" "}
                          <b>Primeiras 2 violações</b> = Suave (posição fechada
                          automaticamente, conta continua).{" "}
                          <b>3ª violação = Severa</b> → conta encerrada.
                        </p>
                      ) : (
                        <p className="flex-1">
                          <b>Funding: </b>
                          Risk per trade must stay ≤ <b>0.5 %</b> of the
                          account’s starting balance. <b>First 2 breaches</b> =
                          Soft (position auto-closed, account continues).{" "}
                          <b>3rd breach = Hard</b> → account ends.
                        </p>
                      )}
                    </div>

                    <hr className="w-full border-t border-white/10 my-[1.5rem]" />

                    <div className="flex items-start gap-[1rem]">
                      <div className="w-[0.75rem] h-[.75rem] bg-white/80 mt-[1rem]" />
                      {lang === "de" ? (
                        <p className="flex-1">
                          <b>Instant Funding Accounts: </b>
                          Risiko pro Trade darf maximal <b>0,5 %</b> des Kontos
                          betragen.{" "}
                          <b>Erste 2 Verstöße = Soft, 3. Verstoß = Hard</b> →{" "}
                          Konto endet.
                        </p>
                      ) : lang === "br" ? (
                        <p className="flex-1">
                          <b>Contas com financiamento instantâneo: </b>O risco
                          por operação deve permanecer ≤ <b>0,5%</b> da conta.{" "}
                          <b>
                            As duas primeiras violações = Leve, a 3ª violação =
                            Dura
                          </b>
                          → a conta é encerrada.
                        </p>
                      ) : (
                        <p className="flex-1">
                          <b>Instant Funding Accounts: </b>
                          Risk per trade must stay ≤ <b>0.5 %</b> of the
                          account.{" "}
                          <b>First 2 breaches = Soft, 3rd breach = Hard </b>→{" "}
                          account ends.
                        </p>
                      )}
                    </div>
                  </div>
                </div>,
              )}

              {popover(
                <div className="relative rounded-full border cursor-pointer group p-[0.625rem] border-[#C4C4C4] h-[2.2rem] w-[2.2rem] hover:bg-[#07171B] hover:border-[#EFEFEF]">
                  <p className="absolute top-0 left-1/2 m-0 uppercase -translate-x-1/2 group-hover:text-white text-[#9A9A9A] text-[1.5rem]">
                    ?
                  </p>
                </div>,
                <div className="text-white shadow-lg p-[2rem] bg-[#07171B] z-[9999] rounded-[1.5rem]">
                  <div className="[&_p]:m-0 [&_p]:text-[2rem] [&_p]:font-sf w-[35.5rem]">
                    <div className="flex items-start gap-[1rem]">
                      <div className="w-[0.75rem] h-[.75rem] bg-white/80 mt-[1rem]" />
                      {lang === "de" ? (
                        <p className="flex-1">
                          <b>Evaluation (Challenge): </b>
                          Risiko pro Trade darf maximal 1 % des Startkapitals
                          betragen. <b>Breach = Soft</b> → die verletzende
                          Position wird automatisch geschlossen, das Konto
                          bleibt aktiv.
                        </p>
                      ) : lang === "br" ? (
                        <p className="flex-1">
                          <b>Avaliação (Desafio): </b>O risco por operação deve
                          permanecer ≤ 1% do saldo inicial da conta.
                          <b> Brecha = Leve</b> → violação A posição é fechada
                          automaticamente, a conta permanece ativa.
                        </p>
                      ) : (
                        <p className="flex-1">
                          <b>Evaluation (Challenge): </b>
                          Risk per trade must stay ≤ 1 % of the account’s
                          starting balance. <b>Breach = Soft</b> → violating
                          position is auto-closed, account remains active.
                        </p>
                      )}
                    </div>

                    <hr className="w-full border-t border-white/10 my-[1.5rem]" />

                    <div className="flex items-start gap-[1rem]">
                      <div className="w-[0.75rem] h-[.75rem] bg-white/80 mt-[1rem]" />
                      {lang === "de" ? (
                        <p className="flex-1">
                          <b>Funding: </b>
                          Risiko pro Trade darf maximal <b>0,5 %</b> des
                          Startkapitals betragen. <b>Erste 2 Verstöße = Soft</b>{" "}
                          (Position wird automatisch geschlossen, Konto bleibt
                          aktiv). <b>3. Verstoß = Hard</b> → Konto endet.
                        </p>
                      ) : lang === "br" ? (
                        <p className="flex-1">
                          <b>Financiamento: </b>O risco por operação deve
                          permanecer ≤ <b>0,5%</b> do saldo inicial da conta.{" "}
                          <b>Primeiras 2 violações</b> = Suave (posição fechada
                          automaticamente, conta continua).{" "}
                          <b>3ª violação = Severa</b> → conta encerrada.
                        </p>
                      ) : (
                        <p className="flex-1">
                          <b>Funding: </b>
                          Risk per trade must stay ≤ <b>0.5 %</b> of the
                          account’s starting balance. <b>First 2 breaches</b> =
                          Soft (position auto-closed, account continues).{" "}
                          <b>3rd breach = Hard</b> → account ends.
                        </p>
                      )}
                    </div>

                    <hr className="w-full border-t border-white/10 my-[1.5rem]" />

                    <div className="flex items-start gap-[1rem]">
                      <div className="w-[0.75rem] h-[.75rem] bg-white/80 mt-[1rem]" />
                      {lang === "de" ? (
                        <p className="flex-1">
                          <b>Instant Funding Accounts: </b>
                          Risiko pro Trade darf maximal <b>0,5 %</b> des Kontos
                          betragen.{" "}
                          <b>Erste 2 Verstöße = Soft, 3. Verstoß = Hard</b> →{" "}
                          Konto endet.
                        </p>
                      ) : lang === "br" ? (
                        <p className="flex-1">
                          <b>Contas com financiamento instantâneo: </b>O risco
                          por operação deve permanecer ≤ <b>0,5%</b> da conta.{" "}
                          <b>
                            As duas primeiras violações = Leve, a 3ª violação =
                            Dura
                          </b>
                          → a conta é encerrada.
                        </p>
                      ) : (
                        <p className="flex-1">
                          <b>Instant Funding Accounts: </b>
                          Risk per trade must stay ≤ <b>0.5 %</b> of the
                          account.{" "}
                          <b>First 2 breaches = Soft, 3rd breach = Hard </b>→{" "}
                          account ends.
                        </p>
                      )}
                    </div>
                  </div>
                </div>,
              )}
            </div>

            {!quickComparison && (
              <div className="flex items-center lg:col-span-5 lg:justify-center lg:border gap-[2rem] rounded-[1.5rem] px-[2rem] py-[.75rem] lg:border-[#DEDEDE80] lg:rounded-[2rem] lg:py-[2.5rem]">
                <p className="mb-0 text-[2rem] font-[500] text-black/80">
                  {selectedAccount?.maxTradeRiskPercentage}%{" "}
                  <span className="mx-2 opacity-50">|</span>$
                  {(
                    ((selectedAccount?.balance ?? 0) *
                      (selectedAccount?.maxTradeRiskPercentage ?? 0)) /
                    100
                  ).toLocaleString("en-US")}
                </p>
              </div>
            )}

            {quickComparison && (
              <>
                {data
                  .filter((each) => each.type === selectedFundingMode)
                  .map((price, index) => (
                    <div
                      key={index}
                      className="flex justify-center items-center border lg:col-span-1 border-[#DEDEDE80] rounded-[1.5rem] py-[2rem] lg:rounded-[2rem]"
                    >
                      <p className="mb-0 text-black/70 text-[2rem] font-[500]">
                        {price?.maxTradeRiskPercentage}%{" "}
                        <span className="mx-2 opacity-50">|</span>$
                        {(
                          ((price?.balance ?? 0) *
                            (price?.maxTradeRiskPercentage ?? 0)) /
                          100
                        ).toLocaleString("en-US")}
                      </p>
                    </div>
                  ))}
              </>
            )}
          </div>
          {/* end trade risk */}
          {/* max consistency rule */}
          <div className="grid w-full border lg:grid-cols-7 lg:p-0 lg:border-none border-[#DEDEDE80] rounded-[1.5rem] p-[1rem] lg:gap-[1.5rem]">
            <div className="flex justify-between items-center lg:col-span-2 lg:border gap-[1.5rem] rounded-[1.5rem] px-[2rem] py-[.75rem] lg:border-[#DEDEDE80] lg:rounded-[2rem] lg:py-[2.5rem]">
              <span className="font-sf text-[2.05rem] font-[600] text-[#2C2C2C]">
                {lang !== "br" ? "Consistency Rule" : "Regra de Consistência"}
              </span>

              {tooltip(
                <div className="relative rounded-full border cursor-pointer group p-[0.625rem] border-[#C4C4C4] h-[2.2rem] w-[2.2rem] hover:bg-[#07171B] hover:border-[#EFEFEF]">
                  <p className="absolute top-0 left-1/2 m-0 uppercase -translate-x-1/2 group-hover:text-white text-[#9A9A9A] text-[1.5rem]">
                    ?
                  </p>
                </div>,
                <div className="text-white shadow-lg p-[2rem] bg-[#07171B] z-[9999] rounded-[1.5rem]">
                  <p className="m-0 text-[2rem] w-[35.5rem]">
                    {lang !== "br"
                      ? `Your single best winning day must never exceed for example
                      ${selectedAccount?.consistencyRulePercentage}% of your
                      cumulative net profit. If it does, the system automatically
                      raises your profit target so that the ratio drops back to ≤
                      ${selectedAccount?.consistencyRulePercentage}%. Nothing
                      breaks—you simply have to keep trading until enough
                      additional profit dilutes that outlier.`
                      : `Seu melhor dia de lucro individual nunca deve exceder, por exemplo,
                      ${selectedAccount?.consistencyRulePercentage}% do seu
                      lucro líquido acumulado. Se isso acontecer, o sistema automaticamente
                      aumenta sua meta de lucro para que a proporção volte a ser ≤
                      ${selectedAccount?.consistencyRulePercentage}%. Nada
                      quebra — você simplesmente precisa continuar negociando até que o lucro adicional suficiente
                      dilua esse valor discrepante.`}
                  </p>
                </div>,
              )}

              {popover(
                <div className="relative rounded-full border cursor-pointer group p-[0.625rem] border-[#C4C4C4] h-[2.2rem] w-[2.2rem] hover:bg-[#07171B] hover:border-[#EFEFEF]">
                  <p className="absolute top-0 left-1/2 m-0 uppercase -translate-x-1/2 group-hover:text-white text-[#9A9A9A] text-[1.5rem]">
                    ?
                  </p>
                </div>,
                <div className="text-white shadow-lg p-[2rem] bg-[#07171B] z-[9999] rounded-[1.5rem]">
                  <p className="m-0 text-[2rem] w-[35.5rem]">
                    {lang !== "br"
                      ? `Your single best winning day must never exceed for example
                      ${selectedAccount?.consistencyRulePercentage}% of your
                      cumulative net profit. If it does, the system automatically
                      raises your profit target so that the ratio drops back to ≤
                      ${selectedAccount?.consistencyRulePercentage}%. Nothing
                      breaks—you simply have to keep trading until enough
                      additional profit dilutes that outlier.`
                      : `Seu melhor dia de lucro individual nunca deve exceder, por exemplo,
                      ${selectedAccount?.consistencyRulePercentage}% do seu
                      lucro líquido acumulado. Se isso acontecer, o sistema automaticamente
                      aumenta sua meta de lucro para que a proporção volte a ser ≤
                      ${selectedAccount?.consistencyRulePercentage}%. Nada
                      quebra — você simplesmente precisa continuar negociando até que o lucro adicional suficiente
                      dilua esse valor discrepante.`}
                  </p>
                </div>,
              )}
            </div>

            {!quickComparison && (
              <div className="flex items-center lg:col-span-5 lg:justify-center lg:border gap-[2rem] rounded-[1.5rem] px-[2rem] py-[.75rem] lg:border-[#DEDEDE80] lg:rounded-[2rem] lg:py-[2.5rem]">
                <span className="font-sf text-[2rem] font-[500] text-black/80">
                  {selectedAccount?.consistencyRulePercentage}%
                </span>
              </div>
            )}

            {quickComparison && (
              <>
                {data
                  .filter((each) => each.type === selectedFundingMode)
                  .map((price, index) => (
                    <div
                      key={index}
                      className="flex justify-center items-center border lg:col-span-1 border-[#DEDEDE80] rounded-[1.5rem] py-[2rem] lg:rounded-[2rem]"
                    >
                      <span className="text-black/70 text-[2rem] font-[500]">
                        {price?.consistencyRulePercentage}%
                      </span>
                    </div>
                  ))}
              </>
            )}
          </div>
          {/* end consistency rule */}
        </div>
        {/* end table */}
        {showFlashSaleBanner && (
          <div className="relative overflow-clip bg-[#FFF7E2] rounded-[3rem] px-[8rem] lg:px-[8.7rem] pt-[4.75rem] pb-[23.5rem] lg:py-[7rem] lg:min-h-[23.5rem] flex flex-col lg:flex-row items-center lg:justify-between">
            <Image
              src={flashSalebg}
              alt=""
              className="hidden lg:block absolute z-0 top-0 left-0 !h-full !w-full"
            />
            <Image
              src={flashSalebgM}
              alt=""
              className="lg:hidden absolute z-0 top-0 left-0 !h-full !w-full"
            />
            <Image
              src={flashSaleStatue}
              alt=""
              className={cn(
                "absolute bottom-0 right-1/2 translate-x-1/2 lg:translate-x-0 lg:right-[38rem] z-0 !w-[34.75rem]",
                lang === "de"
                  ? "lg:right-[38rem]"
                  : lang === "en"
                    ? "lg:right-[43rem]"
                    : "lg:right-[38rem]",
              )}
            />

            <div className="lg:w-fit relative z-[1] flex flex-col gap-[1.5rem] mb-[2.87rem] lg:mb-0">
              {flashSaleBannerTitle && (
                <div
                  className="richtext richtext-center lg:richtext-start w-full
                  prose-h3:text-[3rem] lg:prose-h3:text-[3.85rem] prose-h3:mb-[3rem] prose-h3:leading-[-0.09rem]"
                >
                  <PortableText
                    value={flashSaleBannerTitle}
                    components={richTextComponent}
                  />
                </div>
              )}
              {flashSaleBannerText && (
                <div
                  className="richtext richtext-center lg:richtext-start
                  prose-p:text-[1.75rem] lg:prose-p:text-[2.125rem] prose-p:text-[#103037]/80 prose-strong:font-normal prose-strong:text-[#103037]"
                >
                  <PortableText
                    value={flashSaleBannerText}
                    components={richTextComponent}
                  />
                </div>
              )}
            </div>

            {flashSaleBannerCTA && (
              <div className="flex relative z-10 justify-center">
                <CTAButton data={flashSaleBannerCTA} />
              </div>
            )}
          </div>
        )}

        {!quickComparison && (
          <>
            {/* discount offer */}
            <div className={cn("relative rounded-[2rem] p-[2.5rem] mt-[4rem] sm:p-[3rem] lg:rounded-[3rem] lg:p-[4rem]", futures ? "bg-[#060626]" : "bg-[#07171B]")}>
              {selectedAccount?.currentOfferDiscountPercentage && (
                <div className="absolute left-1/2 -translate-x-1/2 top-[-2.5rem] bg-[#6DA991] w-fit rounded-[1.65rem] px-[2.625rem] py-[1.125rem]">
                  <p className="m-0 text-black font-sf leading-[150%]">
                    {selectedAccount?.currentOfferDiscountPercentage}%{" "}
                    {lang === "de"
                      ? "Rabatt"
                      : lang === "br"
                        ? "de Desconto"
                        : "OFF"}
                  </p>
                </div>
              )}
              <div className="flex flex-col bg-white/5 rounded-[1.5rem] py-[4rem] px-[2rem] sm:py-[5rem] lg:rounded-[2.25rem] lg:px-[5rem]">
                <div className="mb-[2rem]">
                  <h3 className={cn("text-[5rem] font-gestura leading-[135%] mb-0 text-center text-[700] tracking-[-1.2px]", futures ? "text-[#6D72A9]" : "text-[#6DA991]")}>
                    {lang === "de"
                      ? "Preise"
                      : lang === "br"
                        ? "Preços"
                        : "Pricing"}
                  </h3>
                </div>

                <div className="flex flex-col justify-between items-start w-full lg:flex-row lg:gap-0 lg:items-center gap-[1rem] mb-[2rem]">
                  <span className="mx-auto lg:mx-0 px-[2rem] py-[1.5rem] bg-white/5 w-fit rounded-[1.5rem] text-white/80 font-[500] mb-[1rem]">
                    <p className="m-0 leading-[100%] font-instrument text-[2.25rem]">
                      {lang === "de"
                        ? "Kontogröße"
                        : lang === "br"
                          ? "Financiamento"
                          : "Funding"}
                    </p>
                  </span>

                  {/* mobile */}
                  <p
                    className={cn(
                      "lg:hidden w-max lg:text-[9rem] text-[6rem] leading-none font-[500] mb-0 text-white lg:text-start text-center mx-auto lg:mx-0",
                      lang === "de" ? "flex flex-row-reverse" : "flex",
                    )}
                  >
                    <span>$</span>
                    {selectedAccount?.balance
                      ? `${selectedAccount.balance / 1000}k`
                      : ""}
                  </p>
                  {/* end mobile */}

                  <Image
                    src={futures ? arrowRowVFutures : arrowRowV}
                    alt=""
                    className="mx-auto lg:hidden mt-[3rem]"
                  />

                  <div className="flex items-center mx-auto lg:mx-0 lg:mt-0 px-[2rem] py-[1.5rem] bg-white/5 rounded-[1.5rem] gap-[1rem] w-fit mt-[4rem] mb-[1rem]">
                    <span className="font-instrument text-white/80 text-[2.25rem] font-[500]">
                      <p className="m-0 leading-[100%]">
                        {stepPriceText ||
                          (lang === "de"
                            ? "Preis für Monat"
                            : lang === "br"
                              ? "Preço por mês"
                              : "Price per Month")}
                      </p>
                    </span>

                    {tooltip(
                      <div className="relative rounded-full border cursor-pointer group p-[0.625rem] border-[#C4C4C4] h-[2.2rem] w-[2.2rem] hover:bg-[#07171B] hover:border-[#EFEFEF]">
                        <p className="absolute top-0 left-1/2 m-0 uppercase -translate-x-1/2 group-hover:text-white text-[#9A9A9A] text-[1.5rem]">
                          ?
                        </p>
                      </div>,
                      <div className="[&_p]:m-0 [&_p]:text-[2rem] [&_p]:font-sf w-[25rem] space-y-[1.5rem] bg-[#07171B] rounded-[2rem] p-[1.5rem] border-[#C4C4C4] text-white">
                        <p className="font-[600]">
                          {lang !== "br"
                            ? "Additional Costs"
                            : "Custos Adicionais"}
                        </p>
                        <div className="flex justify-between">
                          <p>
                            {lang !== "br"
                              ? "Reset Fee:"
                              : "Taxa de redefinição:"}
                          </p>
                          <p
                            className={
                              lang === "de" ? "flex flex-row-reverse" : "flex"
                            }
                          >
                            <span>$</span>
                            {selectedAccount?.resetFee?.toLocaleString("en-US")}
                          </p>
                        </div>
                        <div className="flex justify-between">
                          <p>
                            {lang !== "br"
                              ? "Activation Fee:"
                              : "Taxa de ativaçao:"}
                          </p>
                          <p
                            className={
                              lang === "de" ? "flex flex-row-reverse" : "flex"
                            }
                          >
                            <span>$</span>
                            {selectedAccount?.activationFee?.toLocaleString(
                              "en-US",
                            )}
                          </p>
                        </div>
                      </div>,
                    )}

                    {popover(
                      <div className="relative rounded-full border cursor-pointer group p-[0.625rem] border-[#C4C4C4] h-[3rem] w-[3rem] hover:bg-[#07171B] hover:border-[#EFEFEF]">
                        <span className="absolute top-1/2 left-1/2 m-0 uppercase -translate-x-1/2 -translate-y-1/2 group-hover:text-white text-[#9A9A9A] text-[2rem]">
                          ?
                        </span>
                      </div>,
                      <div className="[&_p]:m-0 [&_p]:text-[2rem] [&_p]:font-sf w-[25rem] space-y-[1.5rem] bg-[#07171B] rounded-[2rem] p-[1.5rem] border-[#C4C4C4] text-white">
                        <p className="font-[600]">
                          {lang !== "br"
                            ? "Additional Costs"
                            : "Custos Adicionais"}
                        </p>
                        <div className="flex justify-between">
                          <p>
                            {lang !== "br"
                              ? "Reset Fee:"
                              : "Taxa de redefinição:"}
                          </p>
                          <p
                            className={
                              lang === "de" ? "flex flex-row-reverse" : "flex"
                            }
                          >
                            <span>$</span>
                            {selectedAccount?.resetFee?.toLocaleString("en-US")}
                          </p>
                        </div>
                        <div className="flex justify-between">
                          <p>
                            {lang !== "br"
                              ? "Activation Fee:"
                              : "Taxa de ativaçao:"}
                          </p>
                          <p
                            className={
                              lang === "de" ? "flex flex-row-reverse" : "flex"
                            }
                          >
                            <span>$</span>
                            {selectedAccount?.activationFee?.toLocaleString(
                              "en-US",
                            )}
                          </p>
                        </div>
                      </div>,
                    )}
                  </div>

                  {/* mobile */}
                  <div className="flex items-end mx-auto lg:hidden gap-[1rem]">
                    {selectedAccount?.currentOfferDiscountPercentage && (
                      <p
                        className={cn(
                          "m-0 line-through opacity-50 text-[3rem] font-[500] leading-none text-white",
                          lang === "de" ? "flex flex-row-reverse" : "flex",
                        )}
                      >
                        <span>$</span>
                        {(
                          (selectedAccount?.monthlyPrice ?? 0) +
                          (noDailyDrawDown
                            ? (selectedAccount?.upsellNoDailyDrawDown ?? 0)
                            : 0)
                        )?.toLocaleString("en-US")}
                      </p>
                    )}

                    <p
                      className={cn(
                        "m-0 text-[6rem] font-[500] leading-none text-white ",
                        lang === "de" ? "flex flex-row-reverse" : "flex",
                      )}
                    >
                      <span>$</span>
                      {selectedAccount?.currentOfferDiscountPercentage
                        ? (
                            selectedAccount?.monthlyPrice -
                            (selectedAccount?.currentOfferDiscountPercentage /
                              100) *
                              selectedAccount?.monthlyPrice +
                            (noDailyDrawDown
                              ? selectedAccount?.upsellNoDailyDrawDown
                              : 0)
                          )?.toLocaleString("en-US")
                        : (
                            (selectedAccount?.monthlyPrice ?? 0) +
                            (noDailyDrawDown
                              ? (selectedAccount?.upsellNoDailyDrawDown ?? 0)
                              : 0)
                          )?.toLocaleString("en-US")}
                    </p>
                  </div>
                  {/* end mobile */}
                </div>

                <div className="hidden justify-between items-center w-full lg:flex lg:gap-0 gap-[2rem]">
                  <p
                    className={cn(
                      "w-max text-[9rem] font-[500] mb-0 leading-none flex-shrink-0 text-white",
                      lang === "de" ? "flex flex-row-reverse" : "flex",
                    )}
                  >
                    <span>$</span>
                    {selectedAccount?.balance
                      ? `${selectedAccount.balance / 1000}k`
                      : ""}
                  </p>

                  <div className="w-full px-[4rem]">
                    <Image src={futures ? arrowRowFutures : arrowRow} alt="" className="w-full" />
                  </div>

                  <div className="flex flex-shrink-0 items-end gap-[1rem]">
                    {selectedAccount?.currentOfferDiscountPercentage && (
                      <p
                        className={cn(
                          "m-0 line-through opacity-50 mb-[1rem] text-[4rem] leading-none font-[500] text-white",
                          lang === "de" ? "flex flex-row-reverse" : "flex",
                        )}
                      >
                        <span>$</span>
                        {(
                          (selectedAccount?.monthlyPrice ?? 0) +
                          (noDailyDrawDown
                            ? (selectedAccount?.upsellNoDailyDrawDown ?? 0)
                            : 0)
                        )?.toLocaleString("en-US")}
                      </p>
                    )}

                    <p
                      className={cn(
                        "m-0 text-[9rem] font-[500] leading-none  text-white",
                        lang === "de" ? "flex flex-row-reverse" : "flex",
                      )}
                    >
                      <span>$</span>
                      {selectedAccount?.currentOfferDiscountPercentage
                        ? (
                            selectedAccount?.monthlyPrice -
                            (selectedAccount?.currentOfferDiscountPercentage /
                              100) *
                              selectedAccount?.monthlyPrice +
                            (noDailyDrawDown
                              ? selectedAccount?.upsellNoDailyDrawDown
                              : 0)
                          )?.toLocaleString("en-US")
                        : (
                            (selectedAccount?.monthlyPrice ?? 0) +
                            (noDailyDrawDown
                              ? (selectedAccount?.upsellNoDailyDrawDown ?? 0)
                              : 0)
                          )?.toLocaleString("en-US")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* end discount offer */}
          </>
        )}
      </div>
    </TooltipProvider>
  );
}
